import React from "react";
import { useParams, useLocation, useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { money, STATUS_LABELS, PAYMENT_LABELS } from "@/lib/format";
import { CheckCircle2, Heart, Store, Truck, Building2 } from "lucide-react";

const METHOD = {
  pickup: { icon: Store, label: "Local Pickup" },
  local_delivery: { icon: Truck, label: "Local Delivery" },
  carmax_delivery: { icon: Building2, label: "CarMax Workplace Delivery" },
};

export default function Confirmation() {
  const { order_number } = useParams();
  const { state } = useLocation();
  const nav = useNavigate();
  const order = state?.order;

  if (!order) {
    return (
      <div className="max-w-lg mx-auto px-4 py-24 text-center">
        <CheckCircle2 className="text-gold mx-auto mb-4" size={54} />
        <h1 className="font-serif text-3xl text-cream mb-3">Order #{order_number} placed!</h1>
        <p className="text-cream/60 mb-6">Track your order status any time.</p>
        <Button className="btn-gold rounded-full px-8 py-6" onClick={() => nav("/track")}>Track Order</Button>
      </div>
    );
  }

  const m = METHOD[order.fulfillment_method];
  const d = order.fulfillment_details || {};

  return (
    <div className="max-w-2xl mx-auto px-4 py-12">
      <div className="text-center mb-8">
        <CheckCircle2 className="text-gold mx-auto mb-4" size={56} />
        <h1 className="font-serif text-3xl sm:text-4xl font-bold text-cream">Order Confirmed!</h1>
        <p className="font-mono text-gold text-2xl mt-2" data-testid="confirmation-order-number">Order #{order.order_number}</p>
      </div>

      <div className="gold-border-card rounded-2xl bg-card-surface p-6 space-y-5" data-testid="confirmation-panel">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div><p className="text-cream/50">Customer</p><p className="text-cream font-medium">{order.customer.first_name} {order.customer.last_name}</p></div>
          <div><p className="text-cream/50">Phone</p><p className="text-cream font-medium">{order.customer.phone}</p></div>
          <div><p className="text-cream/50">Payment Status</p><p className="text-gold font-medium">{PAYMENT_LABELS[order.payment_status]}</p></div>
          <div><p className="text-cream/50">Order Status</p><p className="text-cream font-medium">{STATUS_LABELS[order.order_status]}</p></div>
        </div>

        <div className="border-t border-gold/20 pt-4">
          <div className="flex items-center gap-2 text-gold mb-2">{m && <m.icon size={18} />}<span className="font-semibold">{m?.label}</span></div>
          {order.fulfillment_method === "local_delivery" && <p className="text-cream/70 text-sm">{d.street}{d.apt ? `, ${d.apt}` : ""}, {d.city}, {d.state} {d.zip}</p>}
          {order.fulfillment_method === "carmax_delivery" && <p className="text-cream/70 text-sm">{d.department} · {d.delivery_day} {d.delivery_time}</p>}
        </div>

        <div className="border-t border-gold/20 pt-4 space-y-2">
          {order.items.map((it, i) => (
            <div key={i} className="flex justify-between text-sm text-cream/80">
              <span>{it.quantity}× {it.category === "cookie" ? `Cookie Pair (${it.size_label})` : `${it.name} — ${it.size_label}`}</span>
              <span className="font-mono">{money(it.line_total)}</span>
            </div>
          ))}
        </div>

        <div className="border-t border-gold/20 pt-4 space-y-1 text-sm">
          <div className="flex justify-between text-cream/70"><span>Subtotal</span><span className="font-mono">{money(order.subtotal)}</span></div>
          {order.discount > 0 && <div className="flex justify-between text-emerald-400"><span>Discount</span><span className="font-mono">-{money(order.discount)}</span></div>}
          {order.delivery_fee > 0 && <div className="flex justify-between text-cream/70"><span>Delivery Fee</span><span className="font-mono">{money(order.delivery_fee)}</span></div>}
          <div className="flex justify-between text-cream font-semibold text-lg pt-1"><span>Total</span><span className="font-mono text-gold">{money(order.total)}</span></div>
        </div>
      </div>

      <div className="text-center mt-8">
        <p className="font-script text-gold text-3xl mb-2">Thank you for supporting Overflow Popcorn Co.!</p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center mt-5">
          <Link to={`/track?order=${order.order_number}`}><Button className="btn-gold rounded-full px-7 py-6 font-semibold w-full sm:w-auto" data-testid="confirmation-track-btn">Track My Order</Button></Link>
          <Link to="/shop"><Button variant="outline" className="rounded-full px-7 py-6 border-gold/40 text-cream bg-transparent hover:bg-gold/10 w-full sm:w-auto">Shop More</Button></Link>
        </div>
        <p className="text-cream/40 text-xs mt-6 flex items-center justify-center gap-1"><Heart size={12} className="text-gold fill-gold" /> Made with love in the South</p>
      </div>
    </div>
  );
}
